import type * as React from "react";
import { cn } from "cn";

/**
 * One orb per teammate (Figma "Teammate avatar", Abacor-New 771:12255): a base color, a soft glow
 * and a lighter highlight rising from the bottom, a darker rim and a fine grain. Every value here
 * is one of the five each tone needs; the shape, blur and grain are shared (`teammate-avatar.css`).
 */
export type TeammateTone = {
  /** The orb's body. */
  base: string;
  /** The wide blurred glow in the lower half. */
  glow: string;
  /** The highlight's top and bottom: it fades from near-white into the body. */
  highlight: readonly [string, string];
  /** The inner rim around the edge. */
  rim: string;
};

/**
 * The palette. The first four are the Figma variants exactly. Amber, teal and rose are derived
 * from them for names Figma does not cover, keeping the same relationships: the glow is the base
 * with 40% white, the highlight runs from 78% to 10% white, and the rim is the base 11 points
 * darker and 13 points less saturated.
 */
export const TEAMMATE_PALETTE = {
  orange: { base: "#f39a5c", glow: "#f8c49f", highlight: ["#ffe6d3", "#f5a46e"], rim: "#e07a36" },
  blue: { base: "#6f92ee", glow: "#a7bdf5", highlight: ["#e2eaff", "#7f9ff0"], rim: "#4f74dc" },
  coral: { base: "#ec7a6c", glow: "#f4aba2", highlight: ["#ffe2de", "#ee8a7e"], rim: "#d85a4c" },
  /** Abacor's own: kept for the product, never handed to a teammate by name. */
  green: { base: "#5cc49e", glow: "#a8e3c9", highlight: ["#e2f7ed", "#6bcfa8"], rim: "#b3ab9f" },
  amber: { base: "#f0c266", glow: "#f6daa3", highlight: ["#fcf2dd", "#f1c875"], rim: "#dca941" },
  teal: { base: "#7fd7d1", glow: "#b2e7e3", highlight: ["#e3f6f5", "#8cdbd5"], rim: "#63bbb5" },
  rose: { base: "#e6709b", glow: "#f0a9c3", highlight: ["#f9e0e9", "#e87ea5"], rim: "#cf4f7e" },
} as const satisfies Record<string, TeammateTone>;

export type TeammateToneName = keyof typeof TEAMMATE_PALETTE;

/** The names Figma draws, in the colors it draws them. */
const NAMED: Readonly<Record<string, TeammateToneName>> = {
  ada: "orange",
  iris: "blue",
  rowan: "coral",
  abacor: "green",
};

/** What any other name may be given. Green is left out so no teammate looks like Abacor. */
const BY_NAME = [
  "orange",
  "blue",
  "coral",
  "amber",
  "teal",
  "rose",
] as const satisfies readonly TeammateToneName[];

/**
 * The tone a name always gets: a named teammate's own, otherwise one picked by a stable hash of
 * the name (FNV-1a, case and surrounding space ignored), so the same name is the same color
 * everywhere and on every load.
 */
export function teammateTone(name: string): TeammateToneName {
  const key = name.trim().toLowerCase();
  const named = NAMED[key];
  if (named) return named;
  let hash = 0x811c9dc5;
  for (const char of key) {
    hash ^= char.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193);
  }
  return BY_NAME[(hash >>> 0) % BY_NAME.length] ?? BY_NAME[0];
}

type AvatarStyle = React.CSSProperties & Record<`--avatar-${string}`, string>;

/**
 * A teammate's avatar at any size. Decorative by default, since a name is almost always beside
 * it; pass `label` where it stands alone. `tone` overrides the color the name would get.
 */
export function TeammateAvatar({
  name,
  size = 24,
  tone,
  label,
  className,
}: {
  name: string;
  size?: number;
  tone?: TeammateToneName | undefined;
  label?: string | undefined;
  className?: string | undefined;
}) {
  const colors: TeammateTone = TEAMMATE_PALETTE[tone ?? teammateTone(name)];
  const style: AvatarStyle = {
    "--avatar-size": `${size}px`,
    "--avatar-base": colors.base,
    "--avatar-glow": colors.glow,
    "--avatar-highlight-top": colors.highlight[0],
    "--avatar-highlight-bottom": colors.highlight[1],
    "--avatar-rim": colors.rim,
  };
  return (
    <span
      data-slot="teammate-avatar"
      className={cn("teammate-avatar", className)}
      style={style}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      <span className="teammate-avatar-glow" />
      <span className="teammate-avatar-highlight" />
    </span>
  );
}
